import { useMutation, useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { getBalance, getBusinessWalletBalance, getBusinessWalletHistory, getHistory, processPayment, topUp, topUpBusinessWallet } from "@/features/wallet/api/walletApi";
import type { WalletProcessPaymentPayload, WalletTopUpPayload } from "@/features/wallet/types";

export const walletKeys = {
  balance: ["wallet", "balance"] as const,
  history: ["wallet", "history"] as const,
  businessBalance: (businessId: number | null) => ["wallet", "business", businessId, "balance"] as const,
  businessHistory: (businessId: number | null) => ["wallet", "business", businessId, "history"] as const,
};

/**
 * Rifreskon cache-in e wallet-it pa prekur state lokale optimiste.
 */
export const invalidateWalletQueries = (queryClient: QueryClient): void => {
  void queryClient.invalidateQueries({ queryKey: walletKeys.balance });
  void queryClient.invalidateQueries({ queryKey: walletKeys.history });
};

/**
 * Merr balancen aktuale te wallet-it.
 */
export const useWalletBalance = (options?: { enabled?: boolean }) =>
  useQuery({
    queryKey: walletKeys.balance,
    queryFn: getBalance,
    enabled: options?.enabled ?? true,
  });

/**
 * Merr historikun e transaksioneve.
 */
export const useWalletHistory = () =>
  useQuery({
    queryKey: walletKeys.history,
    queryFn: getHistory,
  });

export const useBusinessWalletBalance = (businessId: number | null) =>
  useQuery({
    queryKey: walletKeys.businessBalance(businessId),
    queryFn: () => getBusinessWalletBalance(businessId ?? 0),
    enabled: businessId !== null,
  });

export const useBusinessWalletHistory = (businessId: number | null) =>
  useQuery({
    queryKey: walletKeys.businessHistory(businessId),
    queryFn: () => getBusinessWalletHistory(businessId ?? 0),
    enabled: businessId !== null,
  });

/**
 * Kryen top-up dhe pastaj e rilexon balancen nga backend-i.
 */
export const useWalletTopUp = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: WalletTopUpPayload) => topUp(payload),
    onSuccess: () => {
      invalidateWalletQueries(queryClient);
    },
  });
};

export const useBusinessWalletTopUp = (businessId: number | null) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: WalletTopUpPayload) => topUpBusinessWallet(businessId ?? 0, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: walletKeys.businessBalance(businessId) });
      void queryClient.invalidateQueries({ queryKey: walletKeys.businessHistory(businessId) });
    },
  });
};

/**
 * Kryen nje debit dhe rifreskon state-in financiar nga DB.
 */
export const useWalletProcessPayment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: WalletProcessPaymentPayload) => processPayment(payload),
    onSuccess: () => {
      invalidateWalletQueries(queryClient);
    },
  });
};
